import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SimplePermissionService } from '@core/auth';
import {
  InventoryItem,
  InventoryItemPriceHistory,
  InventoryService,
  RgCalculatedPricePayload,
} from '@shared/models';
import {
  DxButtonModule,
  DxDataGridModule,
  DxDateBoxModule,
  DxNumberBoxModule,
  DxSelectBoxModule,
  DxTemplateModule,
} from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { InventoryPriceDetailComponent } from '../inventory-price-detail/inventory-price-detail.component';

/** Editable form state for the "rg-calculated" price calculation. */
interface RgCalcFormData {
  acquisition_price: number | null;
  valuation_date: Date;
  acquisition_date: Date | null;
  condition: number;
  accessories_factor: number;
  repairs_factor: number;
  overhaul_factor: number;
  work_quality_factor: number;
  residual_end_percent: number;
  useful_life_years: number | null;
}

/**
 * Reusable price-history table for an InventoryItem. Loads its own data from
 * the InventoryItemPriceCalculation component endpoint whenever itemId changes.
 * Visibility/permission gating is the responsibility of the host.
 *
 * For the "rg-calculated" method it renders an input form above the table whose
 * "Preis Berechnen" button computes the Zeitwert (rg-calculated endpoint) and
 * immediately applies the result to the item (apply endpoint). The updated item
 * is emitted via (itemUpdated) so the host can refresh its form.
 *
 * R19: knowingly unrendered by the test suite. It has no spec of its own, and
 * both of its render sites — `inventory-view`'s price-calculation accordion item
 * and `form-fields`' price-calculation tab — sit behind
 * `*ngIf="canSeePriceCalculation()"`, which reads an `access_token` out of
 * storage. Neither spec seeds one, so the permission answers false and nothing
 * instantiates this component. A pre-existing gap: this component arrived with
 * the 2026-08-05 merge and was already unrendered before the logic-only
 * conversion.
 */
@Component({
  selector: 'app-inventory-price-history',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DxButtonModule,
    DxDataGridModule,
    DxDateBoxModule,
    DxNumberBoxModule,
    DxSelectBoxModule,
    DxTemplateModule,
    InventoryPriceDetailComponent,
  ],
  templateUrl: './inventory-price-history.component.html',
  styles: [
    `
      .rg-calc {
        margin-bottom: 16px;
      }
      .rg-calc__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: 8px 16px;
        margin-bottom: 8px;
      }
      .rg-calc__field label {
        display: block;
        font-size: 12px;
        margin-bottom: 2px;
      }
    `,
  ],
})
export class InventoryPriceHistoryComponent implements OnChanges, OnInit {
  @Input() itemId: number | null = null;
  @Input() item: Partial<InventoryItem> | null = null;

  /** Emitted with the updated item after a successful rg-calculated apply. */
  @Output() itemUpdated = new EventEmitter<InventoryItem>();

  public priceHistory: InventoryItemPriceHistory[] = [];
  public loaded = false;
  public isQuerying = false;
  public isCalculatingInternal = false;
  public isQueryingGeizhals = false;
  public isCalculatingRg = false;
  public isApplying = false;

  /**
   * Id of the history entry currently reflected in the item — the most recently
   * applied one (max details.applied_to_item.at). Its row button is shown as
   * "applied"; all other rows keep the normal apply button.
   */
  public appliedHistoryId: number | null = null;

  // Initialized in the constructor (not as a field initializer): defaultRgFormData()
  // reads conditionFactors/conditionYearOffset, which are declared further down and
  // would still be undefined if this ran during field initialization.
  public rgFormData: RgCalcFormData;

  /** Zubehör factor options (vollständig / weniger / mehr). */
  public accessoriesOptions = [
    { label: 'inventory.priceCalculation.rgForm.accessoriesFull', value: 1 },
    { label: 'inventory.priceCalculation.rgForm.accessoriesLess', value: 0.9 },
    { label: 'inventory.priceCalculation.rgForm.accessoriesMore', value: 1.1 },
  ];

  /**
   * Zustand-Note → Gebrauchswert factor. PLACEHOLDER mapping pending confirmed
   * values from the product owner (the API only multiplies the factors it
   * receives; the note→factor mapping is a frontend concern).
   */
  private readonly conditionFactors: Record<number, number> = {
    1: 1.0,
    2: 0.9,
    3: 0.8,
    4: 0.7,
    5: 0.6,
    6: 0.5,
  };

  /** Zustand-Note → Baujahr fallback offset in years (spec lines 57–64). */
  private readonly conditionYearOffset: Record<number, number> = {
    1: 0,
    2: 2,
    3: 3,
    4: 6,
    5: 8,
    6: 10,
  };

  constructor(
    public inventoryService: InventoryService,
    private permissions: SimplePermissionService,
    private translate: TranslateService
  ) {
    this.rgFormData = this.defaultRgFormData();
  }

  ngOnInit(): void {
    // Pre-translate the Zubehör dropdown labels (dxSelectBox displayExpr cannot
    // run the translate pipe).
    this.accessoriesOptions = this.accessoriesOptions.map((option) => ({
      ...option,
      label: this.translate.instant(option.label),
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemId']) {
      this.load();
    }
    if (changes['item'] || changes['itemId']) {
      this.rgFormData = this.defaultRgFormData();
    }
  }

  /**
   * True when every required rg-calculated field is filled — gates the
   * "Preis Berechnen" button (spec: only clickable when the form is complete).
   */
  public isRgFormComplete(): boolean {
    const f = this.rgFormData;
    return (
      f.acquisition_price != null &&
      f.acquisition_price >= 0 &&
      f.acquisition_date != null &&
      f.valuation_date != null &&
      f.useful_life_years != null &&
      f.useful_life_years >= 1 &&
      f.residual_end_percent != null &&
      f.repairs_factor != null &&
      f.overhaul_factor != null &&
      f.work_quality_factor != null
    );
  }

  public reload(): void {
    this.load();
  }

  /**
   * Whether the current user may manually run the internal price calculation.
   */
  public canCalcInternal(): boolean {
    return (
      this.permissions.hasOneOfRoles(['admin']) ||
      this.permissions.hasPermission(
        'InventoryItemPriceCalculation.internalQuery'
      )
    );
  }

  /**
   * Manually trigger the internal price calculation. The backend dispatches a
   * queued job, so the result appears on the next reload (not immediately).
   */
  public calcInternal(): void {
    if (this.itemId == null || this.isCalculatingInternal) {
      return;
    }
    this.isCalculatingInternal = true;
    this.inventoryService.triggerInternalPriceQuery(this.itemId).subscribe({
      next: (result) => {
        if (result !== null) {
          notify(
            this.translate.instant(
              'inventory.priceCalculation.internalQueryStarted'
            ),
            'success'
          );
        }
      },
      complete: () => {
        this.isCalculatingInternal = false;
      },
      error: () => {
        this.isCalculatingInternal = false;
      },
    });
  }

  /**
   * Whether the current user may trigger a GPT price query.
   */
  public canQueryGpt(): boolean {
    return (
      this.permissions.hasOneOfRoles(['admin']) ||
      this.permissions.hasPermission('InventoryItemPriceCalculation.gptQuery')
    );
  }

  /**
   * Trigger an asynchronous GPT price research for the current item. The result
   * is fetched on the next reload, so we inform the user it may take a moment.
   */
  public queryGpt(): void {
    if (this.itemId == null || this.isQuerying) {
      return;
    }
    this.isQuerying = true;
    this.inventoryService.triggerGptPriceQuery(this.itemId).subscribe({
      next: (result) => {
        if (result !== null) {
          notify(
            this.translate.instant('inventory.priceCalculation.gptQueryStarted'),
            'success'
          );
        }
      },
      complete: () => {
        this.isQuerying = false;
      },
      error: () => {
        this.isQuerying = false;
      },
    });
  }

  /**
   * Whether the current user may trigger a Geizhals price query.
   */
  public canQueryGeizhals(): boolean {
    return (
      this.permissions.hasOneOfRoles(['admin']) ||
      this.permissions.hasPermission(
        'InventoryItemPriceCalculation.geizhalsQuery'
      )
    );
  }

  /**
   * Trigger an asynchronous Geizhals price lookup for the current item. The
   * result is fetched on the next reload, so we inform the user it may take a
   * moment.
   */
  public queryGeizhals(): void {
    if (this.itemId == null || this.isQueryingGeizhals) {
      return;
    }
    this.isQueryingGeizhals = true;
    this.inventoryService.triggerGeizhalsPriceQuery(this.itemId).subscribe({
      next: (result) => {
        if (result !== null) {
          notify(
            this.translate.instant(
              'inventory.priceCalculation.geizhalsQueryStarted'
            ),
            'success'
          );
        }
      },
      complete: () => {
        this.isQueryingGeizhals = false;
      },
      error: () => {
        this.isQueryingGeizhals = false;
      },
    });
  }

  /**
   * Whether the current user may run the rg-calculated price calculation.
   */
  public canCalcRg(): boolean {
    return (
      this.permissions.hasOneOfRoles(['admin']) ||
      this.permissions.hasPermission(
        'InventoryItemPriceCalculation.rgCalculatedQuery'
      )
    );
  }

  /**
   * Compute the Zeitwert (rg-calculated) from the form and store it as a new
   * price-history entry. It is NOT applied to the item automatically — the user
   * applies it explicitly via the "Übernehmen" button in the table. After the
   * calculation the history is reloaded so the new entry (with its apply button)
   * appears.
   */
  public calcRg(): void {
    if (this.itemId == null || this.isCalculatingRg) {
      return;
    }

    const payload = this.buildRgPayload();
    if (payload === null) {
      return;
    }

    this.isCalculatingRg = true;
    this.inventoryService
      .triggerRgCalculatedPriceQuery(this.itemId, payload)
      .subscribe({
        next: (history) => {
          if (history) {
            notify(
              this.translate.instant('inventory.priceCalculation.rgCalculated'),
              'success'
            );
          }
        },
        complete: () => {
          this.isCalculatingRg = false;
          this.load();
        },
        error: () => {
          this.isCalculatingRg = false;
          this.load();
        },
      });
  }

  /**
   * Whether the current user may apply a price-history entry to the item.
   */
  public canApply(): boolean {
    return (
      this.permissions.hasOneOfRoles(['admin']) ||
      this.permissions.hasPermission('InventoryItemPriceCalculation.applyToItem')
    );
  }

  /**
   * Apply a single price-history entry's price to the item: it becomes the
   * continuation_value and the liquidation_value is derived from it on the
   * backend. The updated item is emitted so the host can refresh; the history
   * is reloaded to show the "applied" marker.
   */
  public applyHistory(history: InventoryItemPriceHistory): void {
    if (this.itemId == null || this.isApplying || history?.id == null) {
      return;
    }
    this.isApplying = true;
    this.inventoryService
      .applyPriceHistoryToItem(this.itemId, history.id)
      .subscribe({
        next: (updatedItem) => {
          if (updatedItem) {
            this.itemUpdated.emit(updatedItem);
            notify(
              this.translate.instant('inventory.priceCalculation.applied'),
              'success'
            );
          }
        },
        complete: () => {
          this.isApplying = false;
          this.load();
        },
        error: () => {
          this.isApplying = false;
          this.load();
        },
      });
  }

  /**
   * Maps the form state to the API payload. The five factors are pre-computed
   * here (Zustand-Note → factor, Zubehör dropdown → factor). Returns null when a
   * required value is still missing (the button should be disabled in that case).
   */
  private buildRgPayload(): RgCalculatedPricePayload | null {
    const f = this.rgFormData;
    if (
      f.acquisition_price == null ||
      f.acquisition_date == null ||
      f.useful_life_years == null
    ) {
      return null;
    }
    return {
      acquisition_price: f.acquisition_price,
      valuation_date: this.toIsoDate(f.valuation_date),
      acquisition_date: this.toIsoDate(f.acquisition_date),
      residual_end_percent: f.residual_end_percent,
      useful_life_years: f.useful_life_years,
      condition_factor: this.conditionFactors[f.condition] ?? 1,
      accessories_factor: f.accessories_factor,
      repairs_factor: f.repairs_factor,
      overhaul_factor: f.overhaul_factor,
      work_quality_factor: f.work_quality_factor,
    };
  }

  /** Builds the default form state, pre-filling from the item where possible. */
  private defaultRgFormData(): RgCalcFormData {
    const condition = this.item?.condition ?? 3;
    return {
      acquisition_price: null,
      valuation_date: new Date(),
      acquisition_date: this.defaultAcquisitionDate(condition),
      condition,
      accessories_factor: 1,
      repairs_factor: 1,
      overhaul_factor: 1,
      work_quality_factor: 1,
      residual_end_percent: 5,
      useful_life_years: null,
    };
  }

  /**
   * Pre-fills the acquisition date: the item's construction year (or vehicle
   * first-registration year) when known, otherwise the Zustand-note fallback
   * (current year − offset). January 1st is used when only a year is available.
   */
  private defaultAcquisitionDate(condition: number): Date {
    const year =
      this.item?.construction_year ??
      this.item?.first_registration_year ??
      new Date().getFullYear() - (this.conditionYearOffset[condition] ?? 0);
    return new Date(year, 0, 1);
  }

  /** Formats a Date as a yyyy-MM-dd string (local date, no timezone shift). */
  private toIsoDate(d: Date): string {
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private load(): void {
    this.loaded = false;
    if (this.itemId == null) {
      this.priceHistory = [];
      this.loaded = true;
      return;
    }
    this.inventoryService.loadPriceHistory(this.itemId).subscribe((history) => {
      this.priceHistory = history;
      this.appliedHistoryId = this.computeAppliedHistoryId(history);
      this.loaded = true;
    });
  }

  /**
   * The id of the most recently applied history entry (latest
   * details.applied_to_item.at), or null when none was ever applied.
   */
  private computeAppliedHistoryId(
    history: InventoryItemPriceHistory[]
  ): number | null {
    let bestId: number | null = null;
    let bestAt = Number.NEGATIVE_INFINITY;
    for (const entry of history) {
      const at = (entry.details as { applied_to_item?: { at?: string } } | null)
        ?.applied_to_item?.at;
      const ts = at ? Date.parse(at) : NaN;
      if (!isNaN(ts) && ts >= bestAt) {
        bestAt = ts;
        bestId = entry.id;
      }
    }
    return bestId;
  }
}
